import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle,
  Delete,
  Edit,
  Save,
  Close,
  Refresh,
  NavigateBefore,
  NavigateNext,
  Image as ImageIcon,
  AutoFixHigh,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import TabDescription from './TabDescription';
import 'firebase/firestore';
import axios from 'axios';
import { useRegionContext } from '../../region/RegionContext';

const PAGE_SIZE = 20;

// ─── Venue Alt Text Sub-tab ─────────────────────────────────────────────────

const VenueAltText = () => {
  const { db, endpoints } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [pageCursors, setPageCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const loadPage = useCallback(async (cursor) => {
    try {
      setLoading(true);
      setError(null);

      let query = db.collection('Business')
        .where('bizPic1Alt', '>', '')
        .orderBy('bizPic1Alt')
        .limit(PAGE_SIZE + 1);

      if (cursor) query = query.startAfter(cursor);

      const snap = await query.get();
      const unverified = snap.docs.filter(doc => !doc.data().altTextVerified);
      setHasNextPage(unverified.length > PAGE_SIZE);

      const pageItems = [];
      for (const doc of unverified.slice(0, PAGE_SIZE)) {
        const data = doc.data();
        const altEntries = [];
        for (let i = 1; i <= 5; i++) {
          if (data[`bizPic${i}Alt`]) {
            altEntries.push({
              slot: i,
              altText: data[`bizPic${i}Alt`],
              imageUrl: data[`bizPic${i}`] || '',
            });
          }
        }
        if (altEntries.length > 0) {
          pageItems.push({
            bizId: doc.id,
            bizName: data.bizName || 'Unknown Venue',
            bizSuburb: data.bizSuburb || '',
            bizState: data.bizState || '',
            altEntries,
            _doc: doc,
          });
        }
      }
      setItems(pageItems);
    } catch (err) {
      console.error('Error loading venue alt text:', err);
      setError('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(pageCursors[pageIndex]); }, [pageIndex]); // eslint-disable-line

  const handleRefresh = () => { setPageCursors([null]); setPageIndex(0); loadPage(null); };

  const handleNextPage = () => {
    if (!hasNextPage || items.length === 0) return;
    const newCursors = [...pageCursors];
    if (!newCursors[pageIndex + 1]) newCursors[pageIndex + 1] = items[items.length - 1]._doc;
    setPageCursors(newCursors);
    setPageIndex(pageIndex + 1);
  };

  const handleSaveEdit = async (bizId, slot) => {
    try {
      setActionLoading(`${bizId}:${slot}:edit`);
      await db.collection('Business').doc(bizId).update({ [`bizPic${slot}Alt`]: editValue });
      setItems(prev => prev.map(item => item.bizId !== bizId ? item : {
        ...item, altEntries: item.altEntries.map(e => e.slot === slot ? { ...e, altText: editValue } : e),
      }));
      setEditingKey(null);
      setEditValue('');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerify = async (bizId) => {
    try {
      setActionLoading(`${bizId}:verify`);
      await db.collection('Business').doc(bizId).update({ altTextVerified: true });
      setItems(prev => prev.filter(item => item.bizId !== bizId));
    } catch (err) {
      alert('Failed to verify: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAlt = async (bizId, slot) => {
    if (!window.confirm(`Delete alt text for image slot ${slot}?`)) return;
    try {
      setActionLoading(`${bizId}:${slot}:delete`);
      await db.collection('Business').doc(bizId).update({
        [`bizPic${slot}Alt`]: firebase.firestore.FieldValue.delete(),
      });
      setItems(prev => prev.map(item => {
        if (item.bizId !== bizId) return item;
        const remaining = item.altEntries.filter(e => e.slot !== slot);
        return remaining.length === 0 ? null : { ...item, altEntries: remaining };
      }).filter(Boolean));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateForVenue = async (bizId) => {
    try {
      setActionLoading(`${bizId}:generate`);
      const idToken = await firebase.auth().currentUser.getIdToken();
      await axios.post(endpoints.generateAltText, { type: 'venue', businessId: bizId }, {
        headers: { Authorization: `Bearer ${idToken}` },
        timeout: 120000,
      });
      // Reload the page to show new alt text
      loadPage(pageCursors[pageIndex]);
    } catch (err) {
      alert('Generation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /><Typography sx={{ mt: 2 }}>Loading venue alt text...</Typography></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} size="small">Refresh</Button>
      </Box>

      {items.length === 0 ? (
        <Alert severity="success">All venue alt text has been verified.</Alert>
      ) : (
        <Card>
          <Table aria-label="Venue alt text review">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Venue</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Image</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Slot</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600, width: '40%' }}>Alt Text</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((venue) =>
                venue.altEntries.map((entry, idx) => (
                  <TableRow key={`${venue.bizId}-${entry.slot}`} hover>
                    {idx === 0 && (
                      <TableCell rowSpan={venue.altEntries.length}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{venue.bizName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[venue.bizSuburb, venue.bizState].filter(Boolean).join(', ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {venue.bizId}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                          <Button variant="contained" color="success" size="small"
                            startIcon={actionLoading === `${venue.bizId}:verify` ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                            onClick={() => handleVerify(venue.bizId)} disabled={!!actionLoading}>
                            Verify All
                          </Button>
                          <Button variant="outlined" color="info" size="small"
                            startIcon={actionLoading === `${venue.bizId}:generate` ? <CircularProgress size={14} /> : <AutoFixHigh />}
                            onClick={() => handleGenerateForVenue(venue.bizId)} disabled={!!actionLoading}>
                            Generate Missing
                          </Button>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      {entry.imageUrl ? (
                        <Box component="a" href={entry.imageUrl} target="_blank" rel="noopener noreferrer"
                          sx={{ display: 'block', width: 80, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'grey.300' }}>
                          <Box component="img" src={entry.imageUrl} alt={entry.altText}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        </Box>
                      ) : <ImageIcon sx={{ color: 'grey.400' }} />}
                    </TableCell>
                    <TableCell><Chip label={`Pic ${entry.slot}`} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      {editingKey === `${venue.bizId}:${entry.slot}` ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <TextField value={editValue} onChange={(e) => setEditValue(e.target.value)} multiline minRows={2} maxRows={4} size="small" fullWidth />
                          <IconButton color="primary" size="small" aria-label="Save alt text" onClick={() => handleSaveEdit(venue.bizId, entry.slot)}
                            disabled={actionLoading === `${venue.bizId}:${entry.slot}:edit`}>
                            {actionLoading === `${venue.bizId}:${entry.slot}:edit` ? <CircularProgress size={16} /> : <Save />}
                          </IconButton>
                          <IconButton size="small" aria-label="Cancel editing" onClick={() => { setEditingKey(null); setEditValue(''); }}><Close /></IconButton>
                        </Box>
                      ) : <Typography variant="body2">{entry.altText}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit"><IconButton size="small" aria-label="Edit alt text" onClick={() => { setEditingKey(`${venue.bizId}:${entry.slot}`); setEditValue(entry.altText); }}
                          disabled={!!actionLoading || !!editingKey}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" aria-label="Delete alt text" onClick={() => handleDeleteAlt(venue.bizId, entry.slot)} disabled={!!actionLoading}>
                          {actionLoading === `${venue.bizId}:${entry.slot}:delete` ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                        </IconButton></Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Page {pageIndex + 1}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<NavigateBefore />} onClick={() => setPageIndex(p => p - 1)} disabled={pageIndex === 0}>Previous</Button>
              <Button size="small" endIcon={<NavigateNext />} onClick={handleNextPage} disabled={!hasNextPage}>Next</Button>
            </Box>
          </Box>
        </Card>
      )}
    </Box>
  );
};

// ─── Dish Alt Text Sub-tab ──────────────────────────────────────────────────

const DishAltText = () => {
  const { db, endpoints } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageStack, setPageStack] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const loadPage = useCallback(async (cursor) => {
    try {
      setLoading(true);
      setError(null);

      // Query dishes that have a photo but no alt text yet
      let query = db.collection('DishDetails')
        .where('dishPic', '>', '')
        .orderBy('dishPic')
        .limit(PAGE_SIZE + 1);

      if (cursor) query = query.startAfter(cursor);

      const snap = await query.get();

      // Filter: has photo, no alt text, not verified
      const candidates = snap.docs.filter(doc => {
        const d = doc.data();
        const pic = d.dishPic || d.DishImage || '';
        return pic && !d.dishPicAlt && !d.dishAltTextVerified;
      });

      // Also load dishes that HAVE alt text but aren't verified
      let withAltQuery = db.collection('DishDetails')
        .where('dishPicAlt', '>', '')
        .orderBy('dishPicAlt')
        .limit(PAGE_SIZE);

      const withAltSnap = await withAltQuery.get();
      const withAltDocs = withAltSnap.docs.filter(d => !d.data().dishAltTextVerified);

      // Merge and deduplicate
      const seen = new Set();
      const allDocs = [];
      for (const doc of [...withAltDocs, ...candidates]) {
        if (seen.has(doc.id)) continue;
        seen.add(doc.id);
        allDocs.push(doc);
      }

      setHasNextPage(candidates.length > PAGE_SIZE);
      if (candidates.length > PAGE_SIZE) {
        setLastDoc(candidates[PAGE_SIZE - 1]);
      }

      const items = allDocs.slice(0, PAGE_SIZE).map(doc => {
        const d = doc.data();
        return {
          dishId: doc.id,
          dishName: d.dishName || 'Unknown Dish',
          bizId: d.bizId || '',
          bizName: d.bizName || '',
          dishPic: d.dishPic || d.DishImage || '',
          dishPicAlt: d.dishPicAlt || '',
          hasAlt: !!d.dishPicAlt,
          _doc: doc,
        };
      });

      setDishes(items);
    } catch (err) {
      console.error('Error loading dish alt text:', err);
      setError('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(pageStack[pageIndex]); }, [pageIndex]); // eslint-disable-line

  const handleRefresh = () => { setPageStack([null]); setPageIndex(0); loadPage(null); };

  const handleGenerateForDish = async (dishId) => {
    try {
      setActionLoading(`${dishId}:generate`);
      const idToken = await firebase.auth().currentUser.getIdToken();
      const resp = await axios.post(endpoints.generateAltText, { type: 'dish', dishId }, {
        headers: { Authorization: `Bearer ${idToken}` },
        timeout: 60000,
      });
      if (resp.data.altText) {
        setDishes(prev => prev.map(d => d.dishId === dishId ? { ...d, dishPicAlt: resp.data.altText, hasAlt: true } : d));
      }
    } catch (err) {
      alert('Generation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyDish = async (dishId) => {
    try {
      setActionLoading(`${dishId}:verify`);
      await db.collection('DishDetails').doc(dishId).update({ dishAltTextVerified: true });
      setDishes(prev => prev.filter(d => d.dishId !== dishId));
    } catch (err) {
      alert('Failed to verify: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEdit = async (dishId) => {
    try {
      setActionLoading(`${dishId}:edit`);
      await db.collection('DishDetails').doc(dishId).update({ dishPicAlt: editValue });
      setDishes(prev => prev.map(d => d.dishId === dishId ? { ...d, dishPicAlt: editValue, hasAlt: true } : d));
      setEditingKey(null);
      setEditValue('');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAlt = async (dishId) => {
    if (!window.confirm('Delete alt text for this dish?')) return;
    try {
      setActionLoading(`${dishId}:delete`);
      await db.collection('DishDetails').doc(dishId).update({
        dishPicAlt: firebase.firestore.FieldValue.delete(),
      });
      setDishes(prev => prev.map(d => d.dishId === dishId ? { ...d, dishPicAlt: '', hasAlt: false } : d));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /><Typography sx={{ mt: 2 }}>Loading dish alt text...</Typography></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} size="small">Refresh</Button>
      </Box>

      {dishes.length === 0 ? (
        <Alert severity="success">No dish photos need alt text review.</Alert>
      ) : (
        <Card>
          <Table aria-label="Dish photo alt text review">
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Dish</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Venue</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }}>Photo</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600, width: '35%' }}>Alt Text</TableCell>
                <TableCell scope="col" sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dishes.map((dish) => (
                <TableRow key={dish.dishId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{dish.dishName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {dish.dishId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{dish.bizName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {dish.bizId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dish.dishPic ? (
                      <Box component="a" href={dish.dishPic} target="_blank" rel="noopener noreferrer"
                        sx={{ display: 'block', width: 80, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'grey.300' }}>
                        <Box component="img" src={dish.dishPic} alt={dish.dishPicAlt || dish.dishName}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      </Box>
                    ) : <ImageIcon sx={{ color: 'grey.400' }} />}
                  </TableCell>
                  <TableCell>
                    {editingKey === dish.dishId ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField value={editValue} onChange={(e) => setEditValue(e.target.value)} multiline minRows={2} maxRows={4} size="small" fullWidth />
                        <IconButton color="primary" size="small" aria-label="Save alt text" onClick={() => handleSaveEdit(dish.dishId)}
                          disabled={actionLoading === `${dish.dishId}:edit`}>
                          {actionLoading === `${dish.dishId}:edit` ? <CircularProgress size={16} /> : <Save />}
                        </IconButton>
                        <IconButton size="small" aria-label="Cancel editing" onClick={() => { setEditingKey(null); setEditValue(''); }}><Close /></IconButton>
                      </Box>
                    ) : dish.hasAlt ? (
                      <Typography variant="body2">{dish.dishPicAlt}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No alt text yet</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {!dish.hasAlt && (
                        <Tooltip title="Generate alt text with GPT-4o-mini">
                          <Button variant="outlined" color="info" size="small"
                            startIcon={actionLoading === `${dish.dishId}:generate` ? <CircularProgress size={14} /> : <AutoFixHigh />}
                            onClick={() => handleGenerateForDish(dish.dishId)} disabled={!!actionLoading}>
                            Generate
                          </Button>
                        </Tooltip>
                      )}
                      {dish.hasAlt && (
                        <>
                          <Tooltip title="Verify alt text">
                            <IconButton size="small" color="success" aria-label="Verify alt text" onClick={() => handleVerifyDish(dish.dishId)} disabled={!!actionLoading}>
                              {actionLoading === `${dish.dishId}:verify` ? <CircularProgress size={16} /> : <CheckCircle />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit"><IconButton size="small" aria-label="Edit alt text" onClick={() => { setEditingKey(dish.dishId); setEditValue(dish.dishPicAlt); }}
                            disabled={!!actionLoading || !!editingKey}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete alt text"><IconButton size="small" color="error" aria-label="Delete alt text" onClick={() => handleDeleteAlt(dish.dishId)} disabled={!!actionLoading}>
                            {actionLoading === `${dish.dishId}:delete` ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                          </IconButton></Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Page {pageIndex + 1}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<NavigateBefore />} onClick={() => setPageIndex(p => p - 1)} disabled={pageIndex === 0}>Previous</Button>
              <Button size="small" endIcon={<NavigateNext />} onClick={() => {
                if (!hasNextPage) return;
                const newStack = [...pageStack];
                if (!newStack[pageIndex + 1]) newStack[pageIndex + 1] = lastDoc;
                setPageStack(newStack);
                setPageIndex(pageIndex + 1);
              }} disabled={!hasNextPage}>Next</Button>
            </Box>
          </Box>
        </Card>
      )}
    </Box>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const AltTextReview = () => {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Alt Text Review
          </Typography>
          <TabDescription>
            GPT-generated alt text for venue photos (bizPic1Alt–bizPic5Alt) and dish images from the
            Business and DishDetails collections. Unverified items appear here for human review. Edit
            alt text for accuracy and tone, then mark as verified to remove from this queue. Good alt
            text improves accessibility for screen reader users and boosts SEO for public venue pages.
          </TabDescription>
        </Box>
      </Box>

      <Tabs value={subTab} onChange={(e, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Venue Photos" />
        <Tab label="Dish Photos" />
      </Tabs>

      {subTab === 0 && <VenueAltText />}
      {subTab === 1 && <DishAltText />}
    </Box>
  );
};

export default AltTextReview;
